import { merge } from 'lodash';
import { PRESET_FILTER_TYPES, TRAINING_REGISTRATION_TYPES } from '../constants';
import {
  updateMenteesElementMediatorsService,
  updateMenteesElementStatusesService,
} from '../services/roleService';
import {
  getAllPersonsElementStatusService,
  getFiltersService,
  getOrganisationUnitsElementStatusService,
  getPersonsElementStatusService,
} from '../services/statusTableService';
import { registerBulkTrainingService } from '../services/trainingService';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createUserFilterService,
  deleteUserFilterService,
  getUserFiltersService,
  updateUserFilterService,
} from '../services/statusService';

export const getUserFilters = createAsyncThunk(
  'statusTable/getUserFilters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserFiltersService();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createUserFilter = createAsyncThunk(
  'statusTable/createUserFilter',
  async (filter, { dispatch, rejectWithValue }) => {
    try {
      await createUserFilterService({
        type: PRESET_FILTER_TYPES.ELEMENT_STATUS,
        filter,
      });
      return dispatch(getUserFilters());
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteUserFilter = createAsyncThunk(
  'statusTable/deleteUserFilter',
  async (filterId, { dispatch, rejectWithValue }) => {
    try {
      await deleteUserFilterService({
        type: PRESET_FILTER_TYPES.ELEMENT_STATUS,
        id: filterId,
      });
      return dispatch(getUserFilters());
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFilters = createAsyncThunk(
  'statusTable/getFilters',
  async (_, { rejectWithValue }) => {
    try {
      const userFiltersResponse = await getUserFiltersService();
      const response = await getFiltersService();
      return {
        ...response.data,
        presetFilters: userFiltersResponse.data.presetFilters,
      };
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getLimitedPersonsElementStatus = createAsyncThunk(
  'statusTable/getLimitedPersonsElementStatus',
  async (persons, { rejectWithValue }) => {
    try {
      const response = await getPersonsElementStatusService({ persons });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getOrganisationUnitsElementStatus = createAsyncThunk(
  'statusTable/getOrganisationUnitsElementStatus',
  async (units, { rejectWithValue }) => {
    try {
      const response = await getOrganisationUnitsElementStatusService(units);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getPersonsElementStatus = createAsyncThunk(
  'statusTable/getPersonsElementStatus',
  async () => {
    const response = await getAllPersonsElementStatusService();
    return response.data;
  }
);

export const updateElementsStatus = createAsyncThunk(
  'statusTable/updateElementsStatus',
  async (
    { personElements, allKnowledgeElements },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        const elements = personElements[personId].map((elementId) => ({
          menteeId: personId,
          knowledgeElementId: elementId,
          knowledgeAreaId:
            allKnowledgeElements[elementId].connections.knowledgeArea,
          isCompleted: true,
        }));
        return [...acc, ...elements];
      }, []);

      await updateMenteesElementStatusesService({ elements: data });
      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addElementsAdminApproval = createAsyncThunk(
  'statusTable/addElementsAdminApproval',
  async (
    { personElements, note, validFromDate },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        return [
          ...acc,
          { menteeId: personId, elements: personElements[personId] },
        ];
      }, []);

      await registerBulkTrainingService({
        type: TRAINING_REGISTRATION_TYPES.ADMIN_APPROVAL,
        note,
        validFromDate,
        operations: data,
      });

      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const completeElements = createAsyncThunk(
  'statusTable/completeElements',
  async ({ personElements }, { dispatch, rejectWithValue }) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        return [
          ...acc,
          { menteeId: personId, elements: personElements[personId] },
        ];
      }, []);

      await registerBulkTrainingService({
        type: TRAINING_REGISTRATION_TYPES.STANDARD,
        operations: data,
      });

      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addElementsStatusReset = createAsyncThunk(
  'statusTable/addElementsStatusReset',
  async ({ personElements, note }, { dispatch, rejectWithValue }) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        return [
          ...acc,
          { menteeId: personId, elements: personElements[personId] },
        ];
      }, []);

      await registerBulkTrainingService({
        type: TRAINING_REGISTRATION_TYPES.RESET_STATUS,
        note,
        operations: data,
      });

      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addElementsAdminStatusReset = createAsyncThunk(
  'statusTable/addElementsAdminStatusReset',
  async (
    { personElements, note, validFromDate },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        return [
          ...acc,
          { menteeId: personId, elements: personElements[personId] },
        ];
      }, []);

      await registerBulkTrainingService({
        type: TRAINING_REGISTRATION_TYPES.ADMIN_RESET_STATUS,
        note,
        validFromDate,
        operations: data,
      });

      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateElementsMediator = createAsyncThunk(
  'statusTable/updateElementsMediator',
  async (
    { personElements, allKnowledgeElements, mediator },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const personIds = Object.keys(personElements);
      const data = personIds.reduce((acc, personId) => {
        const elements = personElements[personId].map((elementId) => ({
          menteeId: personId,
          knowledgeElementId: elementId,
          knowledgeAreaId:
            allKnowledgeElements[elementId].connections.knowledgeArea,
          mediatorId: mediator,
        }));

        return [...acc, ...elements];
      }, []);

      await updateMenteesElementMediatorsService({ elements: data });

      return dispatch(getLimitedPersonsElementStatus(personIds));
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserFilter = createAsyncThunk(
  'statusTable/updateUserFilter',

  async ({ id, filter }, { dispatch, rejectWithValue }) => {
    try {
      await updateUserFilterService({
        type: PRESET_FILTER_TYPES.ELEMENT_STATUS,
        id,
        filter,
      });
      return dispatch(getUserFilters());
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// slice
const statusTableSlice = createSlice({
  name: 'statusTable',
  initialState: {
    categories: {},
    knowledgeAreas: {},
    knowledgeElements: {},
    organisationUnits: {},
    persons: {},
    roles: {},
    wildcardPersons: {},
    presetFilters: {},
    userFilters: [],
    organisationUnitRootNodes: {},
    isLoading: false,
    hasLoadedStatus: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUserFilters.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserFilters.fulfilled, (state, action) => {
        state.presetFilters = action.payload.presetFilters;
        state.isLoading = false;
      })
      .addCase(getUserFilters.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createUserFilter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createUserFilter.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createUserFilter.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteUserFilter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUserFilter.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteUserFilter.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getFilters.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFilters.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.knowledgeAreas = action.payload.knowledgeAreas;
        state.knowledgeElements = action.payload.knowledgeElements;
        state.organisationUnits = action.payload.organisationUnits;
        state.persons = action.payload.employees;
        state.roles = action.payload.roles;
        state.wildcardPersons = action.payload.wildcardPersons;
        state.presetFilters = action.payload.presetFilters;
        state.organisationUnitRootNodes =
          action.payload.organisationUnitRootNodes;
        state.isLoading = false;
      })
      .addCase(getFilters.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getLimitedPersonsElementStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLimitedPersonsElementStatus.fulfilled, (state, action) => {
        state.persons = addElementStatusToPersons(
          state.persons,
          action.payload.persons
        );
        state.hasLoadedStatus = true;
        state.isLoading = false;
      })
      .addCase(getLimitedPersonsElementStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getOrganisationUnitsElementStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOrganisationUnitsElementStatus.fulfilled, (state, action) => {
        state.persons = addElementStatusToPersons(
          state.persons,
          action.payload.persons
        );
        state.hasLoadedStatus = true;
        state.isLoading = false;
      })
      .addCase(getOrganisationUnitsElementStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPersonsElementStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPersonsElementStatus.fulfilled, (state, action) => {
        state.persons = addElementStatusToPersons(
          state.persons,
          action.payload.persons
        );
        state.hasLoadedStatus = true;
        state.isLoading = false;
      })
      .addCase(getPersonsElementStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateElementsStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateElementsStatus.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateElementsStatus.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsAdminApproval.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addElementsAdminApproval.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsAdminApproval.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(completeElements.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(completeElements.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(completeElements.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsStatusReset.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addElementsStatusReset.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsStatusReset.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsAdminStatusReset.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addElementsAdminStatusReset.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addElementsAdminStatusReset.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateElementsMediator.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateElementsMediator.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateElementsMediator.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUserFilter.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserFilter.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateUserFilter.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export default statusTableSlice.reducer;
export const statusTableSliceActions = statusTableSlice.actions;

// helper
const addElementStatusToPersons = (statePersons, personsWithElementStatus) => {
  if (!statePersons || !personsWithElementStatus) {
    return statePersons || {};
  }

  return Object.keys(statePersons).reduce((acc, id) => {
    if (personsWithElementStatus[id]) {
      acc[id] = merge(statePersons[id], personsWithElementStatus[id]);
    } else {
      acc[id] = statePersons[id];
    }
    return acc;
  }, {});
};
